import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const OpsAuthThrottles = () => {
  const [throttles, setThrottles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    email: '',
    ip: '',
    purpose: '',
    org: '',
  });
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [showPruneConfirm, setShowPruneConfirm] = useState(false);

  const loadThrottles = async (nextFilters = filters, nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/support/auth-throttle', {
        params: { ...nextFilters, page: nextPage, page_size: nextPageSize },
      });
      setThrottles(response.data?.throttles || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load auth throttles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThrottles(filters, page, pageSize);
  }, []);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApply = (event) => {
    event.preventDefault();
    setMessage('');
    setPage(1);
    loadThrottles(filters, 1, pageSize);
  };

  const handleReset = () => {
    setMessage('');
    const next = { email: '', ip: '', purpose: '', org: '' };
    setFilters(next);
    setPage(1);
    loadThrottles(next, 1, pageSize);
  };

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const handlePageChange = (nextPage) => {
    const clamped = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(clamped);
    loadThrottles(filters, clamped, pageSize);
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/acct-mgmt/v1/support/auth-throttle', {
        params: { ...filters, page, page_size: pageSize, export: 'csv' },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'auth-throttle.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export auth throttles.');
    }
  };

  const clearThrottles = async (payload) => {
    setMessage('');
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/support/auth-throttle/clear', payload);
      setMessage(`${response.data?.cleared || 0} throttle record(s) cleared.`);
      loadThrottles(filters);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear throttles.');
    }
  };

  const handlePrune = async () => {
    setShowPruneConfirm(false);
    setError('');
    setMessage('');
    try {
      const response = await api.post('/acct-mgmt/v1/support/prune', { target: 'auth_throttle' });
      setMessage(`${response.data?.cleared || 0} record(s) pruned.`);
      loadThrottles(filters, page, pageSize);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to prune auth throttles.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Auth Throttle</h2>
          <div className="text-muted">Review and clear active auth throttles.</div>
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={handleExport}>
          <i className="fas fa-file-csv me-2" aria-hidden="true"></i>
          Export CSV
        </button>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {message && <div className="alert alert-success" role="alert">{message}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Filters</h5>
          <form className="row g-3 align-items-end" onSubmit={handleApply}>
            <div className="col-12 col-md-4">
              <label className="form-label">Email</label>
              <input
                type="text"
                className="form-control"
                value={filters.email}
                onChange={handleChange('email')}
                placeholder="user@domain.com"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">IP Address</label>
              <input
                type="text"
                className="form-control"
                value={filters.ip}
                onChange={handleChange('ip')}
                placeholder="203.0.113.4"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Purpose</label>
              <input
                type="text"
                className="form-control"
                value={filters.purpose}
                onChange={handleChange('purpose')}
                placeholder="signup, login_otp, password_login"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Organization</label>
              <input
                type="text"
                className="form-control"
                value={filters.org}
                onChange={handleChange('org')}
                placeholder="Org name or ID"
              />
            </div>
            <div className="col-12 d-flex flex-wrap justify-content-between gap-3">
              <div className="d-flex flex-wrap gap-2">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Apply Filters
                </button>
                <button type="button" className="btn btn-outline-primary" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
                {filters.email && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => clearThrottles({ email: filters.email, purpose: filters.purpose || undefined })}
                  >
                    Clear Email Locks
                  </button>
                )}
                {filters.ip && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => clearThrottles({ ip: filters.ip, purpose: filters.purpose || undefined })}
                  >
                    Clear IP Locks
                  </button>
                )}
              </div>
              <div></div>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="card-title d-flex flex-wrap align-items-center justify-content-between w-100 mb-3">
            <h5 className="mb-0">Auth Throttle Records</h5>
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowPruneConfirm(true)}>
              Prune Now
            </button>
          </div>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : throttles.length === 0 ? (
            <div className="text-muted">No throttle records found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Key Type</th>
                    <th>Key</th>
                    <th>Attempts</th>
                    <th>Window Start</th>
                    <th>Locked Until</th>
                    <th>Organization</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {throttles.map((row) => (
                    <tr key={row.id}>
                      <td>{row.purpose}</td>
                      <td>{row.key_type}</td>
                      <td className="text-truncate" style={{ maxWidth: '240px' }}>{row.key_value}</td>
                      <td>{row.attempts}</td>
                      <td>{new Date(row.window_start).toLocaleString()}</td>
                      <td>{row.locked_until ? new Date(row.locked_until).toLocaleString() : '-'}</td>
                      <td>{row.org_name || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => clearThrottles({
                            key_type: row.key_type,
                            key_value: row.key_value,
                            purpose: row.purpose,
                          })}
                        >
                          Clear
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && total > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
              <div className="text-muted small">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                <span className="small">Page {page} of {totalPages}</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={pageSize}
                  onChange={(e) => {
                    const nextSize = parseInt(e.target.value, 10);
                    setPageSize(nextSize);
                    setPage(1);
                    loadThrottles(filters, 1, nextSize);
                  }}
                >
                  {[50, 100, 200].map((size) => (
                    <option key={size} value={size}>{size}/page</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      {showPruneConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Prune auth throttles?</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowPruneConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This will remove throttle records older than the retention period configured for this environment.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPruneConfirm(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handlePrune}>
                    Prune Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OpsAuthThrottles;
